import { useEffect, useRef } from 'react';
import browserHistory from '~/browserHistory';
import { ModalOrder, useModal } from '~/components/ModalQueue';
import { TnCOffices } from '~/constants/ff.posthog';
import { useAcceptGoTuTerms } from '~/domain/AcceptGoTuTerms';
import {
  OfficeTnCTypeEnum,
  useAcceptOfficeConsentContent,
  useGetOfficeConsentContent,
} from '~/domain/Consents';
import { shouldUseDsoStructure } from '~/growthbook';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { routes } from '~/routes';
import { POSTHOG_EVENTS } from '~/services/posthog';
import { useAppSelector } from '../useAppSelector';
import { useGetSelectedOffice } from '~/domain/SelectedOffice';
import { usePosthog } from '../usePosthog';
import { useTrackEvent } from '../useTrackEvent';

export const useOfficeTnCConsent = () => {
  const user = useUserSelector();
  const pathname = browserHistory.location.pathname;
  const officeId = user?.id ?? '';
  const isFeatureEnabled = useFeatureFlag(TnCOffices);
  const isOnTnCPage = pathname === routes.termsAndConditions;
  const trackEvent = useTrackEvent();
  const posthog = usePosthog();
  const hasTrackedViewedRef = useRef(false);

  const gotuTermsV2 = useAppSelector((state) => state.user.gotuTerms);

  const {
    data: consentContent,
    isLoading,
    refetch,
  } = useGetOfficeConsentContent({
    params: {
      officeId,
      code: OfficeTnCTypeEnum.OFFICE_TNC,
    },
    enabled: isFeatureEnabled && !!officeId,
  });

  const isNeedOldTnC = shouldUseDsoStructure()
    ? !gotuTermsV2?.accepted
    : !user?.office_info?.gotuTerms?.accepted;

  const isNeedNewTnC =
    !!consentContent &&
    (!consentContent.isConsented || consentContent.notConsentedRequirements.length > 0);

  const shouldShow =
    isFeatureEnabled && !isOnTnCPage && !isLoading && isNeedNewTnC;

  const { isActive } = useModal({
    id: ModalOrder.OFFICE_TNC_CONSENT_POPUP,
    condition: shouldShow,
  });

  useEffect(() => {
    if (shouldShow && isActive && !hasTrackedViewedRef.current) {
      hasTrackedViewedRef.current = true;
      trackEvent(POSTHOG_EVENTS.DENTAL_PORTAL_TERMS_POPUP_VIEWED, { source: 'dashboard' });
      posthog.startSessionRecording();
    }
  }, [shouldShow, isActive, trackEvent, posthog]);

  const acceptMutation = useAcceptOfficeConsentContent({});
  const mutateGetSelectedOffice = useGetSelectedOffice(user?.id);

  const acceptGoTuTermsMutation = useAcceptGoTuTerms();

  const handleAccept = async () => {
    trackEvent(POSTHOG_EVENTS.DENTAL_PORTAL_TERMS_POPUP_ACCEPTED, { source: 'dashboard' });
    try {
      if (isNeedNewTnC) {
        await acceptMutation.mutateAsync({
          officeId,
          code: OfficeTnCTypeEnum.OFFICE_TNC,
        });
      }
      if (isNeedOldTnC) {
        await acceptGoTuTermsMutation.mutateAsync({
          officeId: user?.id,
        });
        await mutateGetSelectedOffice.mutateAsync();
      }
    } finally {
      refetch();
      browserHistory.push(routes.dashboardHome);
    }
  };

  const pdfUrl = consentContent?.notConsentedRequirements?.[0]?.url;

  return {
    isActive,
    shouldShow,
    consentContent,
    handleAccept,
    isAccepting: acceptMutation.isPending,
    pdfUrl,
    isLoading,
    isNeedOldTnC,
    isNeedNewTnC,
    isOnTnCPage,
  };
};
